// 重ね付け（同じパーツの2つ目の置き場所を、1つのカテゴリにまとめる）（2026-10-21 に app.js から分けた）
import { conf } from './config.js';
import { tallUI } from './store.js';
import { TEXT, catLabel, catShort, txt } from './text.js';
import { links } from './core/ops.js';

// ---- 重ね付け（同じパーツの2つ目の置き場所を、1つのカテゴリにまとめる） ----
// データでは「ぼうし 2」なども別のカテゴリ（パーツの一覧が同じで、重なりは1つ目のすぐ上）。カテゴリの列には1つ目だけを出し、
// 「1つ目・2つ目」で編集する置き場所を切りかえる（パーツ・色・柄・移動・反転・固定は、選んでいる置き場所のカテゴリに付く）。
// 持ちものは「左手・右手」（右手のカテゴリは左右反転 REVERSE = 1 で、同じ絵を反対の手に持つ）。
// 保存データは今までどおりカテゴリごとの行なので、本物の CHOCO でもそのまま読める。置き場所は2つまで（データの置き場所が2つのため）
// 全身のメーカーのスマホ（tallUI）はまとめない（クラシック版と同じく「ぼうし」「ぼうし2」を別のカテゴリに。切りかえのパネルがキャラにかぶるため）
// ---- 左右でひと組（2026-10-27〜、DRESSUP2 の め・くつした・ガーター） ----
// データでは左・右が別のカテゴリ（パーツの番号は同じ）。列には左だけを「め」などの名前で出し、「両方・左・右」で変える側を選ぶ。
// 両方（はじめ）のあいだは、左に掛けた操作を右にも同じに掛ける（core/ops.ts の links。横の動きと回転は左右対称に）。
// 全身のメーカーのスマホでもまとめる（切りかえは右の列の「左右」のボタン。パネルは出さない）
export const PAIRS: Record<string, string> = conf('pairs', {});
const PAIR_LABEL: Record<string, string> = conf('pairLabels', {});
export const MERGE: Record<string, string> = Object.assign({}, tallUI ? {} : conf('merge', { hat: 'hat2', wing: 'wing2', glove: 'glove2', facepaint: 'facepaint2', bodypaint: 'bodypaint2', ef: 'ef2', left: 'right' }), PAIRS);
export const HANDS: Record<string, boolean | string> = tallUI ? {} : conf('hands', { left: true });
// 「左手・右手」の列の名前の文言のキー（true は「もちもの」＝CHOCO の持ちもの、文字はそのキー＝BIGBANG の てぶくろ。2026-10-28）
function handsLabel(key: string) { const h = HANDS[key]; return typeof h === 'string' ? h : 'items'; }
// 「左手・右手」の置き場所 i（0: 1つ目、1: 2つ目）の名前の文言のキー（mark は一覧の印の「左・右」）。
// 1つ目が左手か右手かは絵による（CHOCO の持ちものは 1つ目が左手。BIGBANG のてぶくろは 1つ目の絵が向かって左＝キャラの右手なので設定 handsFirstRight）
const FIRST_RIGHT: string[] = conf('handsFirstRight', []);
export function handName(main: string, i: number, mark?: boolean) { const right = (i === 1) !== (FIRST_RIGHT.indexOf(main) >= 0); return (right ? 'hand_r' : 'hand_l') + (mark ? '_mark' : ''); }
export const mergedOf: Record<string, string> = {};  // 2つ目のキー → 1つ目のキー（両方ともデータにあるものだけ。buildCategories で作る）
// 1つ目のキー → いま編集している置き場所（重ね付けは 0: 1つ目・左手、1: 2つ目・右手。左右でひと組は 0: 両方、1: 左、2: 右）
export const layerSlot: Record<string, number> = {};
export function mainKey(key: string) { return mergedOf[key] || key; }
export function slotKeys(key: string): [string, string] | null { const m = mainKey(key); return MERGE[m] && mergedOf[MERGE[m]] === m ? [m, MERGE[m]] : null; }
// 左右でひと組か（両方のデータがあるときだけ）
export function isPair(key: string) { const p = slotKeys(key); return !!p && PAIRS[p[0]] === p[1]; }
// 置き場所から、編集するカテゴリのキー
export function slotKey(pair: [string, string]) { const s = layerSlot[pair[0]] || 0; return pair[PAIRS[pair[0]] === pair[1] ? (s === 2 ? 1 : 0) : (s ? 1 : 0)]; }
// 左右でひと組の「両方」を、操作の掛け方（core/ops.ts の links）に写す。置き場所を変えたら呼ぶ
export function syncLinks() {
  Object.keys(PAIRS).forEach((a) => { if (mergedOf[PAIRS[a]] === a && !layerSlot[a]) links[a] = PAIRS[a]; else delete links[a]; });
}
// 左右でひと組の列の名前のキー（cat.* のキー。絵もこのキーのものがあれば使う＝両目の絵）
export function pairLabelKey(key: string) { return PAIR_LABEL[key] && isPair(key) ? PAIR_LABEL[key] : ''; }
// 左右でひと組の一覧の絵を、いま変えている側だけにするか（設定 pairThumbSide、2026-10-29〜 KAWAII。ユーザー「両目のときは両目、左目なら左目だけのサムネ」）。
// 「両方」のあいだは今までどおり両方の絵、「左」「右」はその側の絵だけ（片方だけなので大きく描ける）。'l'・'r'・null（両方・ひと組でない・設定なし）
const PAIR_THUMB_SIDE = conf('pairThumbSide', false);
export function pairThumbSide(key: string): 'l' | 'r' | null {
  if (!PAIR_THUMB_SIDE) return null;
  const m = mainKey(key);
  if (!isPair(m)) return null;
  const s = layerSlot[m] || 0;
  return s === 1 ? 'l' : s === 2 ? 'r' : null;
}
// カテゴリの並びから、その側のものだけ（左右でひと組でないものはそのまま。土台に敷くカテゴリを選ぶ lookrules.ts の onBase）
export function sideKeys(keys: string[], side: 'l' | 'r' | null) {
  if (!side) return keys;
  return keys.filter((k) => {
    const isL = !!PAIRS[k], isR = Object.keys(PAIRS).some((l) => PAIRS[l] === k);
    return side === 'l' ? !isR : !isL;
  });
}
// 左右でひと組のもう一方のキー（ひと組でなければ null）
export function pairMate(key: string) { const p = slotKeys(key); return p && isPair(key) ? (key === p[0] ? p[1] : p[0]) : null; }
// 一覧の絵に一緒に描くもう一方（「左」「右」で片方だけにするときは null）
export function thumbMate(key: string) { return pairThumbSide(key) ? null : pairMate(key); }
// キャラの上を押して選ぶときなど: 両方で変えているひと組の右は、左（列のボタン）として扱う
export function pickKey(key: string) { const m = mainKey(key); return m !== key && isPair(m) && !layerSlot[m] ? m : key; }
export function tabLabel(key: string) { return HANDS[key] && slotKeys(key) ? txt('cat.' + handsLabel(key)) : PAIR_LABEL[key] && isPair(key) ? catLabel(PAIR_LABEL[key]) : catLabel(key); }
export function tabShort(key: string) { return HANDS[key] && slotKeys(key) ? (TEXT['short.' + handsLabel(key)] || txt('cat.' + handsLabel(key))) : PAIR_LABEL[key] && isPair(key) ? catShort(PAIR_LABEL[key]) : catShort(key); }
